import React from 'react';
import Layout from '../components/Layout';
import { Mail } from 'lucide-react';

const PrivacyPolicy = () => {
    return (
        <Layout>
            <section className="py-20 bg-white dark:bg-slate-950 transition-colors duration-500">
                <div className="container mx-auto px-4 max-w-4xl">
                    
                    {/* Header */}
                    <div className="mb-12">
                        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
                            Privacy <span className="text-accent">Policy</span>
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400">
                            Last updated: Tue 3 Feb, 2026
                        </p>
                    </div>

                    {/* Content */}
                    <div className="prose prose-slate dark:prose-invert max-w-none">
                        <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed mb-8">
                            EverestCloud respects your privacy and is committed to protecting personal and organizational information entrusted to us.
                        </p>

                        <PolicySection title="Information We Collect">
                            <p>We may collect:</p>
                            <ul>
                                <li>Contact details submitted via forms (name, email, organization)</li>
                                <li>Account-related information for platform access</li>
                                <li>Technical metadata such as IP address, browser type, and access logs</li>
                                <li>Usage and diagnostic data for service improvement</li>
                            </ul>
                            <p>We do not collect sensitive personal data unless explicitly required for service delivery.</p>
                        </PolicySection>

                        <PolicySection title="How We Use Information">
                            <p>Information is used to:</p>
                            <ul>
                                <li>Provide and operate EverestCloud services</li>
                                <li>Improve platform performance and security</li>
                                <li>Communicate service-related updates</li>
                                <li>Meet regulatory and legal obligations</li>
                            </ul>
                        </PolicySection>

                        <PolicySection title="Data Protection & Security">
                            <ul>
                                <li>Data is protected using administrative, technical, and physical safeguards</li>
                                <li>Access is restricted based on role and business necessity</li>
                                <li>Monitoring and audit logging are in place for security purposes</li>
                            </ul>
                        </PolicySection>

                        <PolicySection title="Data Residency">
                            <p>
                                Customer data is stored and processed within EverestCloud&apos;s designated data centers as per contractual agreement and regulatory requirements.
                            </p>
                        </PolicySection>

                        <PolicySection title="Information Sharing">
                            <p>We do not sell personal data. Information may be shared:</p>
                            <ul>
                                <li>With authorized service partners under confidentiality obligations</li>
                                <li>When required by law or regulatory authority</li>
                            </ul>
                        </PolicySection>

                        <PolicySection title="Cookies">
                            <p>
                                EverestCloud websites may use cookies for basic functionality and analytics. You may manage cookies through your browser settings.
                            </p>
                        </PolicySection>

                        <PolicySection title="Policy Updates">
                            <p>
                                This Privacy Policy may be updated periodically. Continued use of our services constitutes acceptance of the updated policy.
                            </p>
                        </PolicySection>

                        {/* Contact */}
                        <div className="mt-12 p-6 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-white/5">
                            <div className="flex items-center gap-3">
                                <div className="p-3 bg-accent/10 rounded-lg">
                                    <Mail className="w-5 h-5 text-accent" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500 dark:text-slate-400">Contact</p>
                                    <a href="mailto:privacy@everestcloud.com.np" className="text-accent hover:underline font-medium">
                                        privacy@everestcloud.com.np
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

const PolicySection = ({ title, children }) => (
    <div className="mb-8">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-accent rounded-full"></span>
            {title}
        </h2>
        <div className="text-slate-600 dark:text-slate-300 leading-relaxed space-y-3 pl-4">
            {children}
        </div>
    </div>
);

export default PrivacyPolicy;
